import { html } from '@meldd/article-editor'
import * as React from 'react'
import {
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Keyboard,
  LayoutChangeEvent,
  Platform,
  RefreshControl,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native'
import AutoHeightWebView from 'react-native-autoheight-webview'
import { WebView, WebViewMessageEvent } from 'react-native-webview'
import { useFeatures } from '../../../../contexts/feature.provider'
import { WebViewWeb, WebViewWebRef } from './web-view'
import * as Sentry from '@sentry/react-native'
import { TutorialWrapper as TutorialComponent } from '../../../../@ui/containers/others/tutorial/tutorial.wrapper'
import { useKeyboard } from '../../../../hooks/keyboard.hook'
import { isAndroidBrowser } from '../../../../utils/browser'

export type SelectionInfo = {
  start: number
  end: number
  html: string
}

export interface EditorContribution {
  start: number
  end: number
  type: number
}

export interface ContributionPosition {
  id: string
  top: number
  height: number
}

interface ArticleEditorProps {
  key?: string
  useContainer?: boolean
  editorMode: 'edit' | 'select'
  onSelection?: (info: SelectionInfo) => void
  onContribution?: (id: string | null) => void
  onHtmlContent?: (html: string, text: string) => void
  contributions?: EditorContribution[]
  refreshing?: boolean
  bottomBarOffset?: number
  onRefresh?: () => void
  onScrollEnd?: (scrollPosition: {
    scrollTop: number
    scrollLeft: number
    viewportHeight: number
    contentHeight: number
  }) => void
  onContributionPositions?: (positions: ContributionPosition[]) => void
  onEditorStateChange?: (state: {
    [action: string]: { active: boolean; enabled: boolean }
  }) => void
  renderToolbar?: (
    editorState: { [action: string]: { active: boolean; enabled: boolean } },
    onMenuItem: (msg: any) => void
  ) => React.ReactNode
}

export interface ArticleEditorRef {
  requestContent: () => Promise<{ html: string; text: string }>
  lockSelection: (value: boolean) => void
  showContributions: (value: boolean) => void
  selectContribution: (id?: string) => void
  selectContributionChange: (change: -1 | 1) => void
  setContributions: (contributions: EditorContribution[]) => void
  setEditorContent: (content: string) => void
  setSelectionFromContribution: (contribution: EditorContribution) => void
  clearSelection: () => void
  dismissKeyboard: () => void
  onMenuItem: (msg: any) => void
}

const ArticleEditorImpl = (
  props: ArticleEditorProps,
  ref: Ref<ArticleEditorRef>
) => {
  const { refreshing, onRefresh } = props
  const editorWebView = useRef<WebView | WebViewWebRef>(null)
  const messageQueue = useRef<string[]>([])
  const [viewHeight, setViewHeight] = useState(0)
  const [initialized, setInitialized] = useState(false)
  const [editorLoaded, setEditorLoaded] = useState(false)
  const containerRef = useRef<View>(null)
  const { showEditorLogs } = useFeatures()
  const { keyboardDisplayed, keyboardHeight } = useKeyboard()
  const syncActions = useRef<{
    [key: string]: (value: any) => void
  }>({})

  /**
   * Check if the script is valid JSON, inject script function will fail silently if is not
   */

  const verifyJSON = (object: object) => {
    // Simulates the eval used on the inject script function
    const injectScriptMock = (args: string) => JSON.parse(args)

    const script = `injectScriptMock(\`${JSON.stringify(object)}\`)`
    try {
      eval(script)
      return true
    } catch (e) {
      console.error(e)
      // Log the error message to identify the problematic character
      let problematicCharacter = ''
      let problematicContext = ''
      if (e instanceof SyntaxError) {
        const position = e.message.match(/position (\d+)/)
        if (position) {
          const pos = +position[1] + 1
          problematicCharacter = script[pos]
          problematicContext = script.slice(pos, pos + 12)
          console.error(`Problematic character: ${script[pos]}`)
        }
      }
      const errorMessage =
        'Invalid JSON on loading perspective: ' +
        script +
        (problematicCharacter
          ? ' Problematic Character is `' +
            problematicCharacter +
            '` and context is `' +
            problematicContext +
            '`'
          : '')

      Sentry.captureMessage(errorMessage)
      console.error(errorMessage)

      return false
    }
  }
  const postMessage = (obj: object) => {
    const valid = verifyJSON(obj)
    if (!valid) {
      return
    }
    const script = `onHostMessage(\`${JSON.stringify(obj)}\`)`

    if (initialized) {
      editorWebView.current?.injectJavaScript(script)

      return
    }

    const scripts = [...messageQueue.current, script]
    messageQueue.current = scripts
  }
  useEffect(() => {
    if (initialized && messageQueue.current.length) {
      for (let script of messageQueue.current) {
        editorWebView.current?.injectJavaScript(script)
      }
      messageQueue.current = []
    }
  }, [initialized])

  useImperativeHandle(
    ref,
    () => {
      return {
        requestContent: () => {
          postMessage({ type: 'request-content' })
          const promise = new Promise<{ html: string; text: string }>(
            (resolve) => {
              syncActions.current['request-content'] = resolve
            }
          )
          return promise
        },
        lockSelection: (value: boolean) =>
          postMessage({ type: 'lock-selection', value }),
        showContributions: (value: boolean) =>
          postMessage({ type: 'show-contributions', value }),
        selectContribution: (id?: string) =>
          postMessage({ type: 'select-contribution', id }),
        setSelectionFromContribution: (value: EditorContribution) =>
          postMessage({ type: 'set-selection', value }),
        setContributions: (contributions: EditorContribution[]) =>
          postMessage({
            type: 'set-contributions',
            contributions,
          }),
        selectContributionChange: (change: -1 | 1) =>
          postMessage({ type: 'select-contribution', change }),
        clearSelection: () => postMessage({ type: 'clear-selection' }),
        setEditorContent: (content: string) => {
          const processedContent = Buffer.from(content).toString('base64')

          postMessage({
            type: 'set-editor-content',
            content: processedContent,
          })
        },
        dismissKeyboard: () => {
          Keyboard.dismiss()
        },
        onMenuItem: (msg: any) => {
          editorWebView.current?.injectJavaScript(
            `onHostMessage('${JSON.stringify(msg)}');`
          )
        },
      }
    },
    [postMessage, editorWebView]
  )
  const init = useMemo(() => {
    return `window.articleEditorInit('${props.editorMode}', ${showEditorLogs})`
  }, [props.editorMode, showEditorLogs])

  const [editorState, setEditorState] = React.useState({})
  const onMessage = (event: WebViewMessageEvent) => {
    if (!event.nativeEvent.data || typeof event.nativeEvent.data !== 'string') {
      return
    }
    if (event.nativeEvent.data.startsWith('setImmediate$')) {
      return
    }
    const message = JSON.parse(event.nativeEvent.data) as {
      type: string
      data: any
      text?: string
    }
    switch (message.type) {
      case 'meldd-menu-bridge':
        setEditorState(message.data)
        props.onEditorStateChange?.(message.data)
        break
      case 'meldd-selection':
        props.onSelection?.(message.data)
        break

      case 'meldd-content':
        props.onHtmlContent?.(message.data, message.text!)
        syncActions.current['request-content']?.({
          html: message.data,
          text: message.text,
        })
        break
      case 'contribution-selected':
        props.onContribution?.(message.data || 'none')
        break
      case 'contribution-positions':
        props.onContributionPositions?.(message.data)
        break
      case 'init-done':
        setInitialized(true)
        break

      case 'scroll':
        props.onScrollEnd?.({
          ...message.data,
          viewportHeight: viewHeight,
          contentHeight: message.data.contentHeight - 32, // Remove the paddings
        })
        break
    }
  }
  const webOnload = () => {
    setEditorLoaded(true)
  }

  useEffect(() => {
    if (editorLoaded && init) {
      editorWebView.current?.injectJavaScript(init)
      if (props.editorMode === 'edit') {
        console.log('injecting background color')
        editorWebView.current?.injectJavaScript(
          `document.getElementById('editor').style.backgroundColor = '#f5f2f7';`
        )
      }
    }
  }, [editorLoaded, init])

  const handleWebViewContainerLayout = (event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout
    if (height > 0) {
      setViewHeight(height)
    }
  }

  /**
   * On website responsive we need to handle how the keyboard is displayed.
   * The tool bar is fixed to the top of the keyboard, in order
   * to be able to see all the content of the editor in android web is required
   * ```
   *   keyboardDisplayed && isAndroidBrowser()
   *               ? props.bottomBarOffset
   *               : 0
   * ```
   */
  return (
    <View style={{ flex: 1 }} ref={containerRef}>
      <TutorialComponent
        style={{ flex: 1 }}
        tutorialId="article_editor"
        offsetHeight={20}
      >
        {Platform.OS == 'web' ? (
          <View
            style={{
              flex: 1,
              marginBottom:
                keyboardDisplayed && isAndroidBrowser()
                  ? props.bottomBarOffset
                  : 0,
            }}
            onLayout={handleWebViewContainerLayout}
          >
            <WebViewWeb
              ref={editorWebView}
              title={'Editor'}
              src={`/editor.html`}
              onLoad={webOnload}
              style={{
                width: '100%',
                height:
                  viewHeight > 0
                    ? `${keyboardDisplayed ? viewHeight : viewHeight}px`
                    : '100%',
                paddingBottom: keyboardDisplayed ? keyboardHeight : 0,
              }}
              onMessage={onMessage}
            />
          </View>
        ) : (
          <ScrollView
            contentContainerStyle={{ height: '100%' }}
            refreshControl={
              onRefresh && (
                <RefreshControl
                  refreshing={refreshing || false}
                  onRefresh={onRefresh}
                />
              )
            }
          >
            <AutoHeightWebView
              hideKeyboardAccessoryView={true}
              keyboardDisplayRequiresUserAction={true}
              // nestedScrollEnabled={true}
              style={[styles.webview, { flex: 1, backgroundColor: 'green' }]}
              ref={editorWebView as any}
              onMessage={onMessage}
              originWhitelist={['*']}
              dataDetectorTypes={'none'}
              domStorageEnabled={false}
              bounces={false}
              javaScriptEnabled={true}
              source={{ html }}
              // scalesPageToFit={true}
              customScript={init}
              viewportContent={
                'width=device-width,user-scalable=1.0,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0'
              }
            />
          </ScrollView>
        )}
      </TutorialComponent>
    </View>
  )
}
export const ArticleEditor = forwardRef<ArticleEditorRef, ArticleEditorProps>(
  ArticleEditorImpl
)
const styles = StyleSheet.create({
  webview: {
    backgroundColor: 'transparent',
  },
  toolbarContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 1000,
  },
})
